import { useMemo, useRef } from 'react';
import L from 'leaflet';
import '../../Shared/Map/leaflet-global';
import 'leaflet.markercluster';
import Map, { type IMap } from '../../Shared/Map/index';
import { TranslationService } from '../../../services/translation.service';
import type { IStopPoint } from '../../../models/stop-point.model';
import './style.scss';
import StopPointMarker from './markers/StopPointMarker';
import type { IStopArea } from '../../../models/stop-area.model';
import type { ICommuterParkingLot } from '../../../models/commuter-parking-lot.model';
import type { ITicketOffice } from '../../../models/ticket-office.model';
import StopAreaClusterMarker from './cluster-markers/stop-area.cluster-marker';
import type { ITariffZoneDetails } from '../../../models/tariff-zone-details.model';
import TariffZoneHelper from './helpers/tariffzone.helper';
import type IBounds from '../../Shared/Map/models/bounds.model';
import type { IGenericLocation } from '../../Shared/Map/markers/GenericLocationMarker';
import GenericLocationMarker from '../../Shared/Map/markers/GenericLocationMarker';
import ClusteredMarkers from '../../Shared/Map/components/ClusteredMarkers';
import StopAreaClusterDivIcon from './markers/stop-area-cluster-div-icon';
import CommuterParkingLotClusterMarker from './cluster-markers/commuter-parking-lots.cluster-marker';
import TicketOfficeClusterMarker from './cluster-markers/ticket-office.cluster-marker';
import { type IMapFiltersSelection } from '../StopAreaSearch/components/MapFilters';
import { DateTime } from 'luxon';

interface IStopAreaMapProps {
  mapClassName?: string;
  stopPoints?: IStopPoint[];
  stopAreas?: IStopArea[];
  commuterParkingLots?: ICommuterParkingLot[];
  ticketOffices?: ITicketOffice[];
  genericMarker?: IGenericLocation;
  tariffZones?: ITariffZoneDetails[];
  highLightedStopAreaGid?: string | undefined;
  isFilterApplied?: boolean | undefined;
  mapFiltersSelection?: IMapFiltersSelection;
}

function StopAreaMap({
  mapClassName = undefined,
  stopPoints = undefined,
  stopAreas = undefined,
  commuterParkingLots = undefined,
  ticketOffices = undefined,
  genericMarker = undefined,
  tariffZones = undefined,
  highLightedStopAreaGid = undefined,
  isFilterApplied = undefined,
  mapFiltersSelection = undefined,
}: IStopAreaMapProps) {
  // === REFS ===
  const mapRef = useRef<IMap>(null);

  function getBounds(): IBounds | undefined {
    if (highLightedStopAreaGid) {
      const stopArea = stopAreas?.find((x) => x.gid === highLightedStopAreaGid);
      if (stopArea && stopArea.latitude && stopArea.longitude) return { bounds: new L.LatLngBounds([[stopArea.latitude, stopArea.longitude]]), method: 'panTo' };
    }
    if (genericMarker) {
      return { bounds: new L.LatLngBounds([[genericMarker.latitude, genericMarker.longitude]]), padding: 0.1, method: 'fitBounds' };
    }
    if (stopPoints) {
      const sps = stopPoints.filter((stopPoint) => stopPoint.latitude && stopPoint.longitude).map((stopPoint) => [stopPoint.latitude!, stopPoint.longitude!]);
      const cpls = (commuterParkingLots ?? []).map((cpl) => [cpl.latitude, cpl.longitude]);
      const tos = (ticketOffices ?? []).map((to) => [to.latitude, to.longitude]);
      return {
        bounds: new L.LatLngBounds([...sps, ...cpls, ...tos].map((x) => x as [number, number])),
        padding: 0.1,
        method: 'fitBounds',
      };
    }
    if (stopAreas && !isFilterApplied) {
      return {
        bounds: new L.LatLngBounds(stopAreas.filter((stopPoint) => stopPoint.latitude && stopPoint.longitude).map((stopArea) => [stopArea.latitude!, stopArea.longitude!])),
        padding: 0,
        method: 'fitBounds',
      };
    }
    return undefined;
  }

  const bounds = getBounds();
  const zoomLevel = highLightedStopAreaGid ? 16 : 14;

  const stopAreaMarkers = mapFiltersSelection?.showStopAreas
    ? (stopAreas?.map(
        (stopArea) => new StopAreaClusterMarker(stopArea, () => window.history.pushState({ path: window.location.origin }, '', `?hp=${stopArea.gid}`), stopArea.gid === highLightedStopAreaGid),
      ) ?? [])
    : [];

  const commuterParkingLotMarkers = commuterParkingLots?.map((parkingLot) => new CommuterParkingLotClusterMarker(parkingLot, DateTime.now())) ?? [];

  const ticketOfficeMarkers = ticketOffices?.map((ticketOffice) => new TicketOfficeClusterMarker(ticketOffice)) ?? [];

  const allClusteredMarkers = [...stopAreaMarkers, ...commuterParkingLotMarkers, ...ticketOfficeMarkers];

  const clusteredMarkers = (
    <ClusteredMarkers markers={allClusteredMarkers} disableClusteringAtZoom={15} key={'clustered-stop-area-markers'} iconCreateFunction={(c) => new StopAreaClusterDivIcon(c)} />
  );

  const stopPointMarkers = stopPoints?.map((stopPoint) => <StopPointMarker stopPoint={stopPoint} key={stopPoint.gid} />);

  const boundsSerialized = useMemo(() => (bounds ? JSON.stringify(bounds) : 'no-bounds'), [bounds]);

  return (
    <Map
      key={boundsSerialized} // NV-7071: Force remount of the map when bounds change
      ref={mapRef}
      clusteredMarkers={[clusteredMarkers]}
      ariaLabel={TranslationService.get('stopareasearchpage.stopareamap')}
      className={`stop-area-map ${mapClassName}`}
      markers={stopAreaMarkers.length === 0 ? stopPointMarkers : undefined}
      extraMarkers={genericMarker ? [<GenericLocationMarker marker={genericMarker} key="generic-marker" />] : undefined}
      bounds={bounds}
      zoomLevel={zoomLevel}
      polygons={tariffZones?.map(TariffZoneHelper.mapTariffZoneDetailsToPolygon)}
    />
  );
}
export default StopAreaMap;
